import React from 'react';
import { useTranslation } from 'react-i18next';
import { derivePrefix, validPrefix } from '../../extensions/bundled/task-manager/taskKeys';
import { prefixChange } from '../../extensions/bundled/task-manager/taskKeyPrefixEdit';

/** The prefix field's problem for the current draft: null, 'invalid' or 'taken'. */
export function keyPrefixError(boardModal, takenPrefixes) {
  const draft = String(boardModal?.keyPrefix || '').trim();
  if (!draft) return null;
  const r = prefixChange({ keyPrefix: boardModal?.savedKeyPrefix ?? null }, draft, takenPrefixes);
  return r.ok ? null : r.error;
}

/**
 * "Task key prefix" in the create / edit board dialog (commit ↔ task links,
 * slice 2): the letters before every task number (WEB in WEB-12). Blank keeps
 * the current prefix (or, on a new board, the one derived from its name).
 * A rename keeps the old prefix working — said under the field.
 */
export default function BoardKeyPrefixField({ boardModal, setBoardModal, takenPrefixes = [] }) {
  const { t } = useTranslation();
  const draft = boardModal.keyPrefix || '';
  const saved = validPrefix(boardModal.savedKeyPrefix);
  const shown = validPrefix(draft) || saved || derivePrefix(boardModal.name, takenPrefixes);
  const error = keyPrefixError(boardModal, takenPrefixes);
  const renamed = boardModal.mode === 'edit' && saved && validPrefix(draft) && validPrefix(draft) !== saved;
  return (
    <div className="board-edit-field">
      <label className="board-edit-label" htmlFor="board-edit-key-prefix">
        {t('taskLinks.prefix.label')}
      </label>
      <input
        id="board-edit-key-prefix"
        type="text"
        className="modal-input board-edit-input board-edit-key-prefix"
        placeholder={shown}
        maxLength={5}
        autoComplete="off"
        spellCheck={false}
        value={draft}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby="board-edit-key-prefix-hint"
        onChange={(e) => {
          const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
          setBoardModal((m) => ({ ...m, keyPrefix: value, keyPrefixTouched: true }));
        }}
      />
      <p id="board-edit-key-prefix-hint" className={`board-edit-hint${error ? ' board-edit-hint--error' : ''}`} role={error ? 'alert' : undefined}>
        {error === 'invalid'
          ? t('taskLinks.prefix.invalid')
          : error === 'taken'
            ? t('taskLinks.prefix.taken', { prefix: validPrefix(draft) })
            : renamed
              ? t('taskLinks.prefix.renamed', { prefix: validPrefix(draft), old: saved })
              : t('taskLinks.prefix.hint', { example: `${shown}-12` })}
      </p>
    </div>
  );
}
